import { html, View } from 'rune-ts';
import klass from './CardCurationView.module.scss';
import { classes } from '@services/MarppleShop/shared/util';
import { font } from '@services/MarppleShop/shared/typography';
import { CardProductView } from '@services/MarppleShop/renderApp/pages/Curation/components/CardProductView.ts';
import { fx, noop, pipe } from '@fxts/core';
import { ProductThumbnail } from '@services/MarppleShop/features/ProductList/type';
import { getResizedUrl } from '@services/MarppleShop/shared/util/image.ts';
import { Confirm } from '@services/MarppleShop/renderApp/components/cells/Confirm/Confirm.ts';
import { preventEscape } from '@services/MarppleShop/shared/util/preventEscape.ts';
import { MShopUtilF } from '@modules/MShop/Util/F/Function/module/MShopUtilF';
import { pushLoginStack } from '@modules/Creator/Login/F/fs';
import { assert } from '@services/MarppleShop/shared/util/assert.ts';
import { updateLikeMarppleShop } from '@modules/MShop/App/Product/Item/F/Function/updateLikeMarppleShop';
import { makeMainUrl } from '@services/MarppleShop/shared/util/url.ts';
import { makeEmptyScreenNavigate } from '@services/MarppleShop/shared/app/navigate.ts';
import { dataStr } from '@services/MarppleShop/shared/util/dataStr.ts';

type TProps =
  | {
      device: 'PC';
      layout: 'Detail';
      id: number;
      thumbnail: string;
      title: string;
      sub_title: string;
      products: {
        thumbnail_info: ProductThumbnail;
        is_liked: boolean;
        store_name: string;
        domain_name: string;
        id: number;
        name: string;
        price: number;
        price_en: number | null;
        price_jp: number | null;
        stores_product_id: number;
        user_id: number | null;
      }[];
    }
  | {
      device: 'Mobile';
      layout: 'Detail';
      id: number;
      thumbnail: string;
      title: string;
      sub_title: string;
      products: {
        thumbnail_info: ProductThumbnail;
        is_liked: boolean;
        store_name: string;
        domain_name: string;
        id: number;
        name: string;
        price: number;
        price_en: number | null;
        price_jp: number | null;
        stores_product_id: number;
        user_id: number | null;
      }[];
      curationDetailUrl?: string;
    }
  | {
      device: 'PC';
      layout: 'Thumbnail';
      id: number;
      thumbnail: string;
      title: string;
      sub_title: string;
    }
  | {
      device: 'Mobile';
      layout: 'Thumbnail';
      id: number;
      thumbnail: string;
      title: string;
      sub_title: string;
    };

/**
 * https://www.figma.com/design/IIaFJhb53ELp9PZ4XMnQWo/%ED%81%90%EB%A0%88%EC%9D%B4%EC%85%98?node-id=2270-137767&m=dev
 */
export class CardCurationView extends View<TProps> {
  /**
   * @see https://github.com/marpple/marpple-web/blob/c69213b737c26d28b5417bc5767aaf4a2b953760/services/MarppleShop/renderApp/components/cells/ProductCard/ProductCardView.ts#L301-L329
   */
  private async handleCardProductLikeClick(product_id: number) {
    assert(this.data.layout === 'Detail');

    const target_curation_product = this.data.products.find((product) => product.id === product_id)!;

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (!target_curation_product.user_id || window.box.sel('is_user->type') == 'TEMP') {
      const confirmed = (await Confirm.open({
        title: ET('mps2::login::need_login::title'),
        message: preventEscape(ET('mps2::login::need_login::message')),
        confirmText: ET('mps2::login::login'),
        cancelText: ET('mps2::signup::close'),
      })) as boolean;

      if (confirmed) {
        if (MShopUtilF.isApp()) {
          pushLoginStack();
        } else {
          location.href = `/${T.lang}/@/login?url=${location.pathname + location.search}`;
        }
      }
    } else {
      try {
        const next_is_liked = !target_curation_product.is_liked;
        updateLikeMarppleShop(!next_is_liked, target_curation_product.stores_product_id);

        this.data.products = this.data.products.map((product) =>
          product.id === product_id ? { ...product, is_liked: next_is_liked } : product,
        );
        this.subViews(CardProductView)
          .filter((card_product_view) => card_product_view.data.product_image.id === product_id)
          .forEach((card_product_view) => card_product_view.setProductImageIsLiked(next_is_liked));
      } catch {
        noop();
      }
    }
  }

  protected override template(props: TProps) {
    if (props.device === 'PC' && props.layout === 'Detail') {
      const { sub_title, products, thumbnail, title, id } = props;

      const curation_detail_url = makeMainUrl(`/@/curations/${id}`);

      const curation_detail_data_post_message = pipe(curation_detail_url, makeEmptyScreenNavigate, dataStr);

      return html`<div class="${classes(klass.card_curation, klass.property1_pc)}">
        <a
          class="${klass.thumbnail_wrapper}"
          href="${curation_detail_url}"
          data-post-message="${curation_detail_data_post_message}"
        >
          <img
            alt=""
            src="${getResizedUrl({
              url: thumbnail,
              format: 'webp',
            })}"
            class="${classes(klass.thumbnail, klass.property1_pc)}"
          />
        </a>

        <div class="${classes(klass.preview, klass.property1_pc)}">
          <a
            class="${classes(klass.title, klass.property1_pc)}"
            href="${curation_detail_url}"
            data-post-message="${curation_detail_data_post_message}"
          >
            <p class="${classes(klass.title_text, klass.property1_pc, font('18_semibold'))}">
              ${html.preventEscape(title)}
            </p>

            <p class="${classes(klass.sub_title_text, klass.property1_pc, font('12_medium'))}">
              ${html.preventEscape(sub_title)}
            </p>
          </a>

          <div class="${classes(klass.product, klass.property1_pc)}">
            ${fx(products)
              .take(3)
              .map(
                (product) =>
                  new CardProductView({
                    type: 'A',
                    size: 'Xsmall',
                    layout: 'Vertical',
                    theme: 'Light',
                    state: 'Default',
                    product_image: {
                      domain_name: product.domain_name,
                      thumbnail_info: product.thumbnail_info,
                      resize_width: 126 * 2,
                      is_lazy: true,
                      is_liked: product.is_liked,
                      name: product.name,
                      id: product.id,
                      store_name: product.store_name,
                    },
                    product_title: {
                      id: product.id,
                      domain_name: product.domain_name,
                      name: product.name,
                    },
                    price: {
                      price: product.price,
                      price_en: product.price_en,
                      price_jp: product.price_jp,
                    },
                    onProductImageLikeClick: () => this.handleCardProductLikeClick(product.id),
                  }),
              )
              .toArray()}
          </div>
        </div>
      </div>`;
    } else if (props.device === 'Mobile' && props.layout === 'Detail') {
      const { id, sub_title, title, thumbnail, products, curationDetailUrl } = props;

      const mainCurationDetailUrl = curationDetailUrl ?? makeMainUrl(`/@/curations/${id}`);

      const curation_detail_data_post_message = pipe(mainCurationDetailUrl, makeEmptyScreenNavigate, dataStr);

      return html`<div class="${classes(klass.card_curation, klass.property1_mobile)}">
        <a
          class="${klass.thumbnail_wrapper}"
          href="${mainCurationDetailUrl}"
          data-post-message="${curation_detail_data_post_message}"
        >
          <img
            alt=""
            src="${getResizedUrl({
              url: thumbnail,
              format: 'webp',
            })}"
            class="${classes(klass.thumbnail, klass.property1_mobile)}"
          />
        </a>

        <div class="${classes(klass.preview, klass.property1_mobile)}">
          <a
            class="${classes(klass.title, klass.property1_mobile)}"
            href="${mainCurationDetailUrl}"
            data-post-message="${curation_detail_data_post_message}"
          >
            <p class="${classes(klass.title_text, klass.property1_mobile, font('15_semibold'))}">
              ${html.preventEscape(title)}
            </p>

            <p class="${classes(klass.sub_title_text, klass.property1_mobile, font('11_medium'))}">
              ${html.preventEscape(sub_title)}
            </p>
          </a>

          <div class="${classes(klass.product, klass.property1_mobile)}">
            ${fx(products)
              .take(3)
              .map(
                (product) =>
                  new CardProductView({
                    type: 'A',
                    size: 'Xsmall',
                    layout: 'Vertical',
                    theme: 'Light',
                    state: 'Default',
                    product_image: {
                      domain_name: product.domain_name,
                      thumbnail_info: product.thumbnail_info,
                      resize_width: 101 * 2,
                      is_lazy: true,
                      is_liked: product.is_liked,
                      name: product.name,
                      id: product.id,
                      store_name: product.store_name,
                    },
                    product_title: {
                      id: product.id,
                      domain_name: product.domain_name,
                      name: product.name,
                    },
                    price: {
                      price: product.price,
                      price_en: product.price_en,
                      price_jp: product.price_jp,
                    },
                    onProductImageLikeClick: () => this.handleCardProductLikeClick(product.id),
                  }),
              )
              .toArray()}
          </div>
        </div>
      </div>`;
    } else if (props.device === 'PC' && props.layout === 'Thumbnail') {
      const { sub_title, title, thumbnail, id } = props;

      const curation_detail_url = makeMainUrl(`/@/curations/${id}`);

      return html`<div class="${classes(klass.card_curation, klass.property1_variant2)}">
        <a class="${klass.thumbnail_wrapper}" href="${curation_detail_url}">
          <img
            alt=""
            src="${thumbnail}"
            class="${classes(klass.creator_view_thumbnail, klass.property1_variant2)}"
          />
        </a>

        <a
          class="${classes(klass.creator_view_title, klass.property1_variant2)}"
          href="${curation_detail_url}"
        >
          <p class="${classes(klass.creator_view_title_text, klass.property1_variant2, font('15_semibold'))}">
            ${html.preventEscape(title)}
          </p>

          <p
            class="${classes(klass.creator_view_sub_title_text, klass.property1_variant2, font('12_medium'))}"
          >
            ${html.preventEscape(sub_title)}
          </p>
        </a>
      </div>`;
    } else if (props.device === 'Mobile' && props.layout === 'Thumbnail') {
      const { sub_title, title, thumbnail, id } = props;

      const curation_detail_url = makeMainUrl(`/@/curations/${id}`);

      const curation_detail_data_post_message = pipe(curation_detail_url, makeEmptyScreenNavigate, dataStr);

      return html`<div class="${classes(klass.card_curation, klass.property1_variant3)}">
        <a
          class="${klass.thumbnail_wrapper}"
          href="${curation_detail_url}"
          data-post-message="${curation_detail_data_post_message}"
        >
          <img
            alt=""
            src="${thumbnail}"
            class="${classes(klass.creator_view_thumbnail, klass.property1_variant3)}"
          />
        </a>

        <a
          class="${classes(klass.creator_view_title, klass.property1_variant3)}"
          href="${curation_detail_url}"
          data-post-message="${curation_detail_data_post_message}"
        >
          <p class="${classes(klass.creator_view_title_text, klass.property1_variant3, font('14_semibold'))}">
            ${html.preventEscape(title)}
          </p>

          <p
            class="${classes(klass.creator_view_sub_title_text, klass.property1_variant3, font('12_medium'))}"
          >
            ${html.preventEscape(sub_title)}
          </p>
        </a>
      </div>`;
    } else {
      throw new Error();
    }
  }
}
